import * as React from 'react';

import type { TokenTransparency } from '@blockworks/platform/api/strapi';
import { TrackCallbackType } from '@blockworks/platform/services';

import { SortableTh } from '@/components/pages/token-transparency/components/sortable-th';
import { TokenTransparencyTableRow } from '@/components/pages/token-transparency/components/token-transparency-table-row';
import { tokenTransparencyBounds } from '@/components/pages/token-transparency/token-transparency.model';
import type { TokenTransparencyWithTotals } from '@/components/pages/token-transparency/token-transparency-page-static-props';
import useSortableData from '@/utils/hooks/use-sortable-data';

type TokenTransparencyTableProps = {
    tokenTransparencyReports: TokenTransparencyWithTotals[];
    assets: { [key: string]: { imageUrl: string } };
    track: TrackCallbackType;
};

export const TokenTransparencyTable = (props: TokenTransparencyTableProps) => {
    const { tokenTransparencyReports, assets, track } = props;
    const {
        items: sortedAndFiltered,
        requestSort,
        sortConfig,
    } = useSortableData<TokenTransparency>(tokenTransparencyReports, { key: 'tokenSymbol', direction: 'asc' });

    const mobileHiddenCellClasName =
        'md:w-28 lg:w-38 xl:w-44 py-4 px-4 hidden md:table-cell content-center place-items-center ' +
        'align-center text-center sticky top-0 lg:py-6 align-middle whitespace-nowrap ' +
        'text-left text-sm font-semibold text-gray-900';

    const defaultThClassName =
        'sticky top-0 lg:py-6 align-middle whitespace-nowrap text-left text-sm font-semibold text-gray-900';

    return (
        <div className="border-b">
            <div className="overflow-x-auto ">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr className="text-xxs text-left bg-gray-50">
                            <SortableTh
                                className={`${defaultThClassName} py-4 px-4 md:px-8 pb-4 border-r`}
                                identifier="tokenSymbol"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Project Name"
                            />
                            <SortableTh
                                className={mobileHiddenCellClasName}
                                identifier="projectAndTeamRating"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Project/Team"
                                center
                                tooltipText={
                                    <>
                                        <h4>
                                            Project and Team (Out Of {tokenTransparencyBounds.projectAndTeamRating.max})
                                        </h4>
                                        <p>
                                            Reviews the people, governance structure, and conflict-of-interest controls
                                            that drive the protocol’s day-to-day decisions.
                                        </p>
                                    </>
                                }
                            />
                            <SortableTh
                                className={mobileHiddenCellClasName}
                                identifier="tokenSupplyAndAllocationRating"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Token Allocation"
                                center
                                tooltipText={
                                    <>
                                        <h4>
                                            Token Supply and Allocation (Out Of{' '}
                                            {tokenTransparencyBounds.tokenSupplyAndAllocationRating.max})
                                        </h4>
                                        <p>
                                            Assesses total supply, vesting schedules, and how tokens are distributed
                                            among insiders, the community, and the treasury.
                                        </p>
                                    </>
                                }
                            />
                            <SortableTh
                                className={mobileHiddenCellClasName}
                                identifier="transactionsAndMarketStructureRating"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Market Structure"
                                center
                                tooltipText={
                                    <>
                                        <h4>
                                            Transactions & Market Structure (Out Of{' '}
                                            {tokenTransparencyBounds.transactionsAndMarketStructureRating.max})
                                        </h4>
                                        <p>
                                            Examines trading liquidity, CEX and market-maker agreements, and
                                            related-party transactions that could influence price discovery.
                                        </p>
                                    </>
                                }
                            />
                            <SortableTh
                                className={mobileHiddenCellClasName}
                                identifier="financialDisclosureRating"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Financial Disclosure"
                                center
                                tooltipText={
                                    <>
                                        <h4>
                                            Financial Disclosure (Out Of{' '}
                                            {tokenTransparencyBounds.financialDisclosureRating.max})
                                        </h4>
                                        <p>
                                            Looks at the reporting standards of the project’s financials and operations
                                        </p>
                                    </>
                                }
                            />

                            <SortableTh
                                className={`${defaultThClassName} lg:min-w-24 p-4 lg:table-cell text-center content-center place-items-center align-center border-r border-l bg-gray-50`}
                                identifier="total"
                                sortConfig={sortConfig}
                                requestSort={requestSort}
                                title="Total"
                                center
                                tooltipText={
                                    <>
                                        <h4>Total (Out Of {tokenTransparencyBounds.total.max})</h4>
                                        <p>The overall score, with the highest possible score being 40</p>
                                    </>
                                }
                            />
                            <th
                                className={`${defaultThClassName} content-center place-items-center align-center text-center`}
                            >
                                Filing
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {sortedAndFiltered.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-light-gray p-4 text-sm">
                                    There were no token transparency ratings found matching your filter criteria.
                                </td>
                            </tr>
                        )}

                        {sortedAndFiltered.map(tt => (
                            <TokenTransparencyTableRow key={tt.tokenSymbol} tt={tt} track={track} assets={assets} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
